import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faToggleOn, faToggleOff, faEdit } from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';
import './NumerosEspeciales.css';
import useApiWithLoader from '../../../hooks/useApiWithLoader';

export default function NumerosEspeciales() {
    const [numerosEspeciales, setNumerosEspeciales] = useState({
        numero1: '0000',
        numero2: '1111',
        numero3: '2222',
        numero4: '3333',
        numero5: '4444',
        seccion_activa: true,
        mensaje: 'Si te sale alguno de nuestros números premiados, reclama de inmediato $50 a tasa BCV directo a tu cuenta bancaria.'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { apiCall } = useApiWithLoader();

    useEffect(() => {
        cargarNumerosEspeciales();
    }, []);

    const cargarNumerosEspeciales = async () => {
        await apiCall(async () => {
            try {
                const response = await fetch(`${baseURL}/numerosEspecialesGet.php`, {
                    method: 'GET',
                });
                const data = await response.json();
                
                if (data.success && data.data) {
                        setNumerosEspeciales({
                            numero1: data.data.numero1 || '0000',
                            numero2: data.data.numero2 || '1111',
                            numero3: data.data.numero3 || '2222',
                            numero4: data.data.numero4 || '3333',
                            numero5: data.data.numero5 || '4444',
                            seccion_activa: data.data.seccion_activa == 1,
                            mensaje: data.data.mensaje || 'Si te sale alguno de nuestros números premiados, reclama de inmediato $50 a tasa BCV directo a tu cuenta bancaria.'
                        });
                }
            } catch (error) {
                toast.error('Error al cargar los números especiales');
            } finally {
                setLoading(false);
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validar que solo sean números y máximo 4 dígitos
        if (name.startsWith('numero') && value.length <= 4) {
            const numericValue = value.replace(/[^0-9]/g, '');
            setNumerosEspeciales(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else if (name === 'mensaje') {
            setNumerosEspeciales(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const toggleSeccionActiva = () => {
        setNumerosEspeciales(prev => ({
            ...prev,
            seccion_activa: !prev.seccion_activa
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        // Validar que todos los números tengan exactamente 4 dígitos
        const numeros = [
            numerosEspeciales.numero1,
            numerosEspeciales.numero2,
            numerosEspeciales.numero3,
            numerosEspeciales.numero4,
            numerosEspeciales.numero5
        ];

        for (let i = 0; i < numeros.length; i++) {
            if (numeros[i].length !== 4) {
                toast.error(`El número ${i + 1} debe tener exactamente 4 dígitos`);
                setSaving(false);
                return;
            }
        }

        try {
            const response = await fetch(`${baseURL}/numerosEspecialesPut.php`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    numero1: numerosEspeciales.numero1,
                    numero2: numerosEspeciales.numero2,
                    numero3: numerosEspeciales.numero3,
                    numero4: numerosEspeciales.numero4,
                    numero5: numerosEspeciales.numero5,
                    seccion_activa: numerosEspeciales.seccion_activa ? 1 : 0,
                    mensaje: numerosEspeciales.mensaje
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Números especiales actualizados correctamente');
            } else {
                toast.error(data.error || 'Error al actualizar los números especiales');
            }
        } catch (error) {

            toast.error('Error al actualizar los números especiales');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="numeros-especiales-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando números especiales...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="numeros-especiales-container">
            <ToastContainer />
            
            <div className="header-section">
                <div>
                    <h2>
                        <FontAwesomeIcon icon={faEdit} className="header-icon" />
                        Gestión de Números Especiales
                    </h2>
                    <p>Configura los números especiales que aparecen en la página de inicio y controla la visibilidad de la sección.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="numeros-form">
                <div className="form-section">
                    <h3>Configuración de la Sección</h3>
                    
                    <div className="switch-container">
                        <label className="switch-label">
                            Mostrar sección de números especiales
                        </label>
                        <button
                            type="button"
                            className={`toggle-switch ${numerosEspeciales.seccion_activa ? 'active' : ''}`}
                            onClick={toggleSeccionActiva}
                        >
                            <FontAwesomeIcon 
                                icon={numerosEspeciales.seccion_activa ? faToggleOn : faToggleOff} 
                                className="toggle-icon"
                            />
                            <span className="toggle-text">
                                {numerosEspeciales.seccion_activa ? 'Activada' : 'Desactivada'}
                            </span>
                        </button>
                    </div>

                    <div className="input-group">
                        <label htmlFor="mensaje">Mensaje de la sección:</label>
                        <textarea
                            id="mensaje"
                            name="mensaje"
                            value={numerosEspeciales.mensaje}
                            onChange={handleInputChange}
                            placeholder="Ingresa el mensaje que aparecerá en la sección..."
                            rows="3"
                            className="mensaje-input"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Números Especiales</h3>
                    <p className="section-description">
                        Configura los 5 números especiales que aparecerán en la página de inicio.
                        Cada número debe tener exactamente 4 dígitos.
                    </p>
                    
                    <div className="numeros-grid">
                        <div className="numero-input-group">
                            <label htmlFor="numero1">Número 1:</label>
                            <input
                                type="text"
                                id="numero1"
                                name="numero1"
                                value={numerosEspeciales.numero1}
                                onChange={handleInputChange}
                                maxLength="4"
                                className="numero-input"
                                placeholder="0000"
                            />
                        </div>

                        <div className="numero-input-group">
                            <label htmlFor="numero2">Número 2:</label>
                            <input
                                type="text"
                                id="numero2"
                                name="numero2"
                                value={numerosEspeciales.numero2}
                                onChange={handleInputChange}
                                maxLength="4"
                                className="numero-input"
                                placeholder="1111"
                            />
                        </div>

                        <div className="numero-input-group">
                            <label htmlFor="numero3">Número 3:</label>
                            <input
                                type="text"
                                id="numero3"
                                name="numero3"
                                value={numerosEspeciales.numero3}
                                onChange={handleInputChange}
                                maxLength="4"
                                className="numero-input"
                                placeholder="2222"
                            />
                        </div>

                        <div className="numero-input-group">
                            <label htmlFor="numero4">Número 4:</label>
                            <input
                                type="text"
                                id="numero4"
                                name="numero4"
                                value={numerosEspeciales.numero4}
                                onChange={handleInputChange}
                                maxLength="4"
                                className="numero-input"
                                placeholder="3333"
                            />
                        </div>

                        <div className="numero-input-group">
                            <label htmlFor="numero5">Número 5:</label>
                            <input
                                type="text"
                                id="numero5"
                                name="numero5"
                                value={numerosEspeciales.numero5}
                                onChange={handleInputChange}
                                maxLength="4"
                                className="numero-input"
                                placeholder="4444"
                            />
                        </div>
                    </div>
                </div>

                <div className="preview-section">
                    <h3>Vista Previa</h3>
                    <div className="preview-container">
                        <div className="preview-info">
                            <p>{numerosEspeciales.mensaje}</p>
                        </div>
                        <div className="preview-numbers">
                            <div className="preview-row">
                                <span className="preview-number">{numerosEspeciales.numero1}</span>
                                <span className="preview-number">{numerosEspeciales.numero2}</span>
                            </div>
                            <div className="preview-row">
                                <span className="preview-number">{numerosEspeciales.numero3}</span>
                                <span className="preview-number">{numerosEspeciales.numero4}</span>
                                <span className="preview-number">{numerosEspeciales.numero5}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="submit"
                        className="save-button"
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faSave} className="save-icon" />
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </form>
        </div>
    );
}
